import React, { forwardRef, useImperativeHandle } from "react";
import Editor from "@monaco-editor/react";
import { Form, Spinner, Alert } from "react-bootstrap";
import { AppButton } from "../ui";
import useCodingChallenge, { DEFAULT_CHALLENGE, SANDBOX_EDITOR_OPTIONS } from "./useCodingChallenge";
import { ChevronDown, Play, Terminal } from "lucide-react";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); CodingChallenge.scss deleted with
// it, and its six hardcoded greys are the new --code-* family in _palette.scss.
//
// Three things the stylesheet was doing that are NOT carried over, because each
// was a defect rather than a style:
//
//  1. "Run Code" was a bespoke #3042fc — a blue belonging to no token, on a
//     screen whose other three buttons were react-bootstrap's. All four are
//     AppButton now, so they carry the institute's brand. (Fourth module in this
//     phase found to have grown its own button around an accent colour.)
//  2. `.left-col` had `border-right: 1px solid #232635` — the exact colour of the
//     right column it borders, so that divider has always rendered as nothing.
//     Dropped rather than re-coloured: the #1e1e1e editor already reads as a seam.
//  3. `.dropdown-icon` is rendered by this component and was styled NOWHERE, so
//     the chevron never got a `position: absolute` and flowed after the select
//     instead of sitting inside it. It is positioned here.
//
// And the Test Cases table at the foot was NOT a `.results-table`, so Bootstrap's
// `.table` painted it `--bs-body-bg` with `--bs-emphasis-color` ink — a white
// block with near-black text, inside a dark editor shell. Both tables are plain
// <table> elements now: the four `!important`s on the first one existed only to
// beat that same Bootstrap rule, and with the class gone so is the race.
const SHELL = "tw-overflow-hidden tw-rounded-sm tw-bg-code tw-text-code-fg";

const TOOLBAR =
  "tw-flex tw-items-center tw-gap-4 tw-border-0 tw-border-b tw-border-solid " +
  "tw-border-code-border tw-bg-code-surface tw-px-5 tw-py-2.5";

const BOX_LABEL = "tw-mb-1.5 tw-text-[0.98rem] tw-font-semibold tw-text-code-fg-muted";

const IO_FIELD =
  "tw-mb-[18px] tw-min-h-[110px] tw-resize-y tw-rounded-[4px] tw-border " +
  "tw-border-code-border tw-bg-code-sunken tw-font-mono tw-text-code-fg " +
  "placeholder:tw-text-code-fg placeholder:tw-opacity-100";

const TABLE = "tw-w-full tw-bg-code-surface tw-text-code-fg";
const CELL = "tw-border tw-border-code-border tw-px-2 tw-py-1 tw-align-top";
const STATUS_PILL =
  "tw-inline-block tw-rounded-sm tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-on-status";

const CodingChallenge = forwardRef(({
  question = DEFAULT_CHALLENGE.question,
  sampleCode = DEFAULT_CHALLENGE.sampleCode,
  testCases = DEFAULT_CHALLENGE.testCases,
  challengeId,
  description = DEFAULT_CHALLENGE.description,
  onSubmit,
  disabled
}, ref) => {

  const {
    lang, code, setCode, languages, input, setInput, output, results, running, error, submitted,
    onMount, handleLanguageChange, handleRun, handleRunAllCases, handleSubmit,
  } = useCodingChallenge({ sampleCode, testCases, challengeId, onSubmit });

  useImperativeHandle(ref, () => ({
    runAllTests: handleRunAllCases,
    getCode: () => code,
    setCode: newCode => setCode(newCode),
    getResults: () => results,
    autoRunAndSubmit: async () => {
      await handleRunAllCases("submit");
    }
  }));

    return (
      <>
        <div className="fs-6 fw-semibold mb-2">Question:</div>
        <div className="mb-2 text-lg">
          <div dangerouslySetInnerHTML={{ __html: question }} />
        </div>
        <div className="mb-3 text-md">
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </div>
        <div className={SHELL}>
          <div className={TOOLBAR}>
            <div className="tw-relative">
              <Form.Select
                value={lang}
                className="tw-w-[220px] tw-border-0 tw-bg-code tw-pe-9 tw-text-code-fg"
                onChange={handleLanguageChange}
                style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none" }}
                disabled={languages.length === 0}
              >
                {languages.some(l => sampleCode?.[l.value]) && (
                  <optgroup label="⭐ Recommended">
                    {languages.filter(l => sampleCode?.[l.value]).map(l => (
                      <option key={l.id} value={l.value}>{l.label}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Other Languages">
                  {languages.filter(l => !sampleCode?.[l.value]).map(l => (
                    <option key={l.id} value={l.value}>{l.label}</option>
                  ))}
                </optgroup>
              </Form.Select>
              {/* Dropdown icon — positioned here; `.dropdown-icon` had no rule at all. */}
              <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-code-fg-muted">
                <ChevronDown size="1em" />
              </span>
            </div>
            <AppButton className="tw-ms-auto" onClick={handleRun} disabled={running}>
              {running ? <Spinner size="sm" animation="border" /> : <><Play size="1em" /> Run Code</>}
            </AppButton>
            <AppButton variant="outline" onClick={handleRunAllCases} disabled={running}>
              {running ? <Spinner size="sm" animation="border" /> : <><Terminal size="1em" />Test All Cases</>}
            </AppButton>
            <AppButton
              variant="success"
              onClick={handleSubmit}
              disabled={disabled || !results.length}
            >
              Submit
            </AppButton>
          </div>
          {/* Main Row */}
          <div className="tw-flex tw-min-h-[62vh]">
            {/* Code Editor */}
            <div className="tw-min-w-0 tw-flex-1">
              <Editor
                height="calc(70vh - 40px)"
                language={lang}
                value={code}
                onChange={setCode}
                theme="vs-dark"
                options={SANDBOX_EDITOR_OPTIONS}
                onMount={onMount}
              />
            </div>
            {/* Input/Output */}
            <div className="tw-flex tw-w-[320px] tw-min-w-[280px] tw-flex-col tw-bg-code-surface tw-py-[18px] tw-pe-[14px] tw-ps-[18px]">
              <div>
                <div className={BOX_LABEL}>Input</div>
                <Form.Control
                  as="textarea"
                  rows={7}
                  className={IO_FIELD}
                  placeholder="Enter input here..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  spellCheck={false}
                />
              </div>
              <div>
                <div className={BOX_LABEL}>Output</div>
                <Form.Control
                  as="textarea"
                  rows={7}
                  className={`${IO_FIELD} tw-mb-0`}
                  value={output}
                  readOnly
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
          {/* Results Table */}
          {results.length > 0 && (
            <table className={`${TABLE} tw-mt-6`}>
              <thead className="tw-text-code-fg-muted">
                <tr>
                  <th className={CELL}>#</th>
                  <th className={CELL}>Input</th>
                  <th className={CELL}>Expected Output</th>
                  <th className={CELL}>Your Output</th>
                  <th className={CELL}>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td className={CELL}>{i + 1}</td>
                    <td className={CELL}><pre className="mb-0">{r.input || "stdin empty"}</pre></td>
                    <td className={CELL}><pre className="mb-0">{r.expected}</pre></td>
                    <td className={CELL}><pre className="mb-0">{r.output}</pre></td>
                    <td className={CELL}>
                      <span className={`${STATUS_PILL} ${r.passed ? "tw-bg-chart-2" : "tw-bg-chart-4"}`}>
                        {r.passed ? "Passed" : "Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {submitted && !error && (
            <Alert variant="success" className="mt-3">Code submitted successfully!</Alert>
          )}
          {/* Question & Test Case Info */}
          <div className="tw-p-4">
            <div className="fs-6 fw-semibold mb-2">Test Cases:</div>
            <table className={TABLE}>
              <thead className="tw-text-code-fg-muted">
                <tr>
                  <th className={`${CELL} tw-w-[140px]`}>Input</th>
                  <th className={`${CELL} tw-w-[200px]`}>Expected Output</th>
                </tr>
              </thead>
              <tbody>
                {testCases.filter(tc => !tc.isHidden).map((tc, i) => (
                  <tr key={i}>
                    <td className={CELL}><pre className="mb-0">{tc.input || "stdin empty"}</pre></td>
                    <td className={CELL}><pre className="mb-0">{tc.expected}</pre></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
});

export default CodingChallenge;
